// Copyright (c) 2019 Celadon Development LLC, All rights reserved.
// Author Yana Sidorova <yana.sidorova@celadon.ae>
import { select, put, call, takeEvery } from "redux-saga/effects";
import * as firebase from "firebase/app";
import ReactPixel from "react-facebook-pixel";

import history from "../../../services/history";
import {
  basketService,
  basketSearchService,
  basketRemoveItemService,
  cleanBasketService,
  getCartIdService,
  getIsEligibleForHomePickupService,
  getBasketNotificationsService,
  getBookshopBasketService,
} from "./service";
import { getIsEligibleForHomePickup, getTotal, getBasket } from "./selectors";
import * as basketActions from "./actions";
import * as types from "./actionTypes";
import * as getPaidActions from "../../get-paid/store/actions";

const searchStatus = {
  0: "No offer",
  1: "Offer",
};

function* basketSaga(payload) {
  yield put(basketActions.loadingStart());
  if (payload.isChooseDeliv) {
    const isEligibleForHomePickup = yield select(getIsEligibleForHomePickup);
    if (!isEligibleForHomePickup) {
      const isEligibleForHomePickupFromStorage = yield JSON.parse(
        localStorage.getItem("isEligibleForHomePickup")
      );
      yield put(
        basketActions.setIsEligibleForHomePickup(
          isEligibleForHomePickupFromStorage
        )
      );
    }
  }

  const result = yield call(basketService, { CartID: payload.CartID });
  yield put(basketActions.loadingStop());
  if (result.ok) {
    const basket = result.body.CartDetails;

    const redeemValue = result.body.CartDetails.filter(
      (item) => item.VoucherId === null
    ).reduce((sum, item) => sum + item.SellbackPrice, 0);

    yield put(basketActions.basketRequestSuccess(basket));
    yield put(
      basketActions.setBasketTotal({
        items: basket.length,
        price: result.body.OrderValue || 0,
        orderId: result.body.OrderID || 0,
        redeemValue,
      })
    );

    // A shop's basket holds books from several customers at once, each group
    // sitting under the voucher that customer was issued. The call above
    // carries a VoucherId per line but nothing to name it with, so the shop
    // view comes from the same endpoint the app uses - otherwise the two
    // clients would describe one basket in two different ways.
    if (parseInt(localStorage.getItem("isBookshop"), 10)) {
      const bookshopResult = yield call(getBookshopBasketService, {
        CustomerId: localStorage.getItem("loggedID"),
        Token: localStorage.getItem("token"),
        CartId: payload.CartID,
      });

      // Left alone on failure: a flat basket is a worse view, not a broken
      // one, and the books and prices above are already on screen.
      if (bookshopResult && bookshopResult.ok) {
        yield put(basketActions.setBookshopBasket(bookshopResult.body));
      }
    }
    if (result.body.DiscountCodeInfo !== null) {
      yield put(
        getPaidActions.setActiveDiscountCode(result.body.DiscountCodeInfo)
      );
    }

    const total = yield select(getTotal);
    const wasQualified = Boolean(
      parseInt(localStorage.getItem("wasQualified"), 10)
    );

    if (!wasQualified && (total.price >= 10 || total.items >= 10)) {
      localStorage.setItem("wasQualified", 1);

      firebase.analytics().logEvent("qualified_baskets", {
        customer_id: payload.CustomerId,
        cart_id: payload.CartID,
      });
    }
  } else {
    localStorage.removeItem("CartID");
    yield put(basketActions.basketReset());
  }
}

function* basketRemoveItemSaga(payload) {
  yield put(basketActions.loadingStart());
  const result = yield call(basketRemoveItemService, payload);
  if (result.ok) {
    const basket = yield select(getBasket);
    let totalCost = 0;
    if (basket.length > 1) {
      totalCost =
        basket
          ?.filter(
            (item) => item.OrderDetailsID !== payload.deleteItem.OrderDetailsID
          )
          ?.reduce((acc, item) => acc + item.SellbackPrice, 0) || 0;
    }
    console.log(totalCost);
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: "remove_from_cart",
      cart_value: totalCost, // This field will be used as a custom dimension
      ecommerce: {
        items: [
          {
            item_id: payload.deleteItem.EAN,
            item_name: payload.deleteItem.Title,
            currency: "GBP",
            price: payload.deleteItem.SellbackPrice,
            quantity: 1,
          },
        ],
      },
    });
    yield put(basketActions.loadingStop());
    const CartID = localStorage.getItem("CartID");
    yield put(basketActions.requestBasket(CartID));
  } else {
    yield put(basketActions.loadingStop());
  }
}

function* cleanBasketSaga(payload) {
  yield put(basketActions.loadingStart());

  const basket = yield select(getBasket);
  const redeemedItems = basket.filter((item) => item.VoucherId !== null);

  const res = yield call(cleanBasketService, payload);

  if (res.ok && res.body) {
    const totalCost = basket.reduce((acc, item) => acc + item.SellbackPrice, 0);
    const items = basket.map((item) => {
      return {
        item_id: item.EAN,
        item_name: item.Title,
        currency: "GBP",
        price: item.SellbackPrice,
        quantity: 1,
      };
    });

    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: "remove_from_cart",
      cart_value: totalCost,
      ecommerce: {
        items,
      },
    });

    if (redeemedItems.length === 0) {
      yield put(basketActions.basketReset());
      yield put(getPaidActions.resetActiveDiscountCode());

      localStorage.removeItem("CartID");
      localStorage.setItem("wasQualified", 0);
    } else {
      const { CartID } = payload;
      yield put(basketActions.requestBasket(CartID));
    }

    yield put(basketActions.loadingStop());
  } else {
    yield put(basketActions.loadingStop());
  }
}

function* basketSearchSaga(payload) {
  // const userData = {
  //   CustomerId: localStorage.getItem('loggedID'),
  //   Token: localStorage.getItem('token'),
  // };
  // if (userData.Token === null || userData.Token === undefined || userData.Token === '') {
  //   return yield put(basketActions.basketSearchRequestFailed({ error: 'Not authorized.' }));
  // }

  yield put(basketActions.setDisableGetPriceButton(true));
  yield put(basketActions.loadingStart());
  const CartId = localStorage.getItem("CartID") || "";
  const UserID = localStorage.getItem("loggedID") || "";
  const data = {
    CartID: CartId,
    UserID,
    EAN: payload.EAN,
  };
  const res = yield call(basketSearchService, data);
  yield put(basketActions.loadingStop());
  if (res.ok && res.body) {
    const basket = yield select(getBasket);
    const totalCost =
      basket.reduce((acc, item) => acc + item.SellbackPrice, 0) +
      res?.body?.Price;
    firebase.analytics().logEvent("item_search", {
      customer_id: payload.CustomerId,
      status: searchStatus[res.body.Accepted],
      EAN: payload.EAN,
      search_method: payload.searchMethod,
    });
    ReactPixel.trackCustom("item_search", {
      status: searchStatus[res.body.Accepted],
      EAN: payload.EAN,
    });
    window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
    window.dataLayer.push({
      event: "add_to_cart",
      cart_value: totalCost,
      ecommerce: {
        items: [
          {
            item_id: res.body.EAN,
            item_name: res.body.Title,
            currency: "GBP",
            price: res.body.Price,
            quantity: 1,
          },
        ],
      },
    });
    const { CartID } = res.body;
    if (CartID) {
      yield put(basketActions.changeField(null, "EAN"));
      yield put(basketActions.basketSearchRequestSuccess({ CartID }));
      yield put(basketActions.requestBasket(CartID));
      return localStorage.setItem("CartID", CartID);
    }
  }
  const { Message } = res.body;
  yield put(basketActions.changeField(null, "EAN"));
  return yield put(basketActions.basketSearchRequestFailed({ error: Message }));
}

function* getCartIdSaga() {
  const data = {
    CustomerId: localStorage.getItem("loggedID"),
    Token: localStorage.getItem("token"),
  };
  const res = yield call(getCartIdService, data);
  if (res.ok) {
    localStorage.setItem("CartID", res.body);
  }
}

function* getIsEligibleForHomePickupSaga(payload) {
  const res = yield call(getIsEligibleForHomePickupService, payload);
  if (res.ok) {
    yield localStorage.setItem("isEligibleForHomePickup", res.body);
    yield put(basketActions.setIsEligibleForHomePickup(res.body));
    yield history.push(payload.to);
  }
}

function* getBasketNotificationsSaga(payload) {
  const res = yield call(getBasketNotificationsService, payload);
  if (res.ok) {
    const errors = res.body.map((item) => item.Notification);
    const error = errors.join(". ");
    yield put(basketActions.setBasketNotifyError(error));
  }
}

export function* updateCartIndicatorSaga() {
  yield call(getCartIdSaga);

  const CartID = localStorage.getItem("CartID");
  yield call(basketSaga, { CartID });
}

const basketSagas = [
  takeEvery(types.BASKET_SEARCH_REQUEST, basketSearchSaga),
  takeEvery(types.BASKET_REQUEST, basketSaga),
  takeEvery(types.BASKET_REMOVE_ITEM, basketRemoveItemSaga),
  takeEvery(types.CLEAN_BASKET, cleanBasketSaga),
  takeEvery(types.GET_CART_ID, getCartIdSaga),
  takeEvery(
    types.GET_IS_ELIGIBLE_FOR_HOME_PICKUP,
    getIsEligibleForHomePickupSaga
  ),
  takeEvery(types.GET_BASKET_NOTIFY_ERROR, getBasketNotificationsSaga),
];

export default basketSagas;
